// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { StepComponentProps } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';
import { toIsoDate } from '../../utils/date';

const YES_NO_OPTIONS = [
  { value: 'Yes', text: 'Yes' },
  { value: 'No', text: 'No' },
];

// Select chevron SVG
const chevronIcon = (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Calendar icon SVG
const calendarIcon = (maskId: string) => (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
      <path d="M16 0H0V16H16V0Z" fill="white"/>
    </mask>
    <g mask={`url(#${maskId})`}>
      <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
      <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
      <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
      <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
    </g>
  </svg>
);

interface StepOtherInformationProps extends StepComponentProps {
  policyTypeCategory?: string | null;
}

export function StepOtherInformation({
  data,
  onFieldChange,
  pendingFiles,
  onAddFile,
  onRemoveFile,
  policyTypeCategory,
}: StepOtherInformationProps) {
  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT';
  const policyAssigned = (data.policyAssigned as string) === 'Yes';
  const pensionAdjustment = (data.pensionAdjustmentOrder as string) === 'Yes';

  const inputClass =
    "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";

  const selectClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${hasValue ? "text-[#333]" : "text-[#B6B6B6]"} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const dateInputClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const textareaClass =
    "w-full min-h-[120px] px-md py-[12px] rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal resize-vertical";

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  if (isInvestment) {
    return (
      <div className="flex flex-col gap-lg">
        {/* Title */}
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Other information
        </div>

        {/* Row 1: Has policy/account been assigned or pledged? + To whom? + What date? */}
        <div className="flex flex-row gap-lg items-end">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Has policy/account been assigned or pledged? <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={(data.policyAssigned as string) || ''}
                onChange={(e) => onFieldChange('policyAssigned', e.target.value)}
                className={selectClass(!!(data.policyAssigned as string))}
              >
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {YES_NO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              {chevronIcon}
            </div>
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>To whom?</div>
            <input
              type="text"
              value={(data.policyAssignedTo as string) || ''}
              onChange={(e) => onFieldChange('policyAssignedTo', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>What date?</div>
            <div className="relative">
              <input
                type="date"
                value={(data.policyAssignedDate as string) || ''}
                onChange={(e) => onFieldChange('policyAssignedDate', toIsoDate(e.target.value))}
                className={dateInputClass(!!(data.policyAssignedDate as string))}
              />
              {calendarIcon('mask_inv_assigned_date')}
            </div>
          </div>
        </div>

        {/* ══════════════ Divider ══════════════ */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Row 2: Has policy a bare trust or deed of assignment? + To whom? */}
        <div className="flex flex-row gap-lg items-end">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>
              Has policy a bare trust or deed of assignment? <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={(data.hasBareTrustOrDeed as string) || ''}
                onChange={(e) => onFieldChange('hasBareTrustOrDeed', e.target.value)}
                className={selectClass(!!(data.hasBareTrustOrDeed as string))}
              >
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {YES_NO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              {chevronIcon}
            </div>
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>To whom?</div>
            <input
              type="text"
              value={(data.bareTrustOrDeedTo as string) || ''}
              onChange={(e) => onFieldChange('bareTrustOrDeedTo', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* ══════════════ Divider ══════════════ */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Row 3: Is there a unique death benefit? */}
        <div className="flex flex-col gap-sm">
          <div className={labelClass}>
            Is there a unique death benefit applying to the policy? <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <select
              value={(data.uniqueDeathBenefit as string) || ''}
              onChange={(e) => onFieldChange('uniqueDeathBenefit', e.target.value)}
              className={selectClass(!!(data.uniqueDeathBenefit as string))}
            >
              <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
              {YES_NO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
              ))}
            </select>
            {chevronIcon}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      {/* Title */}
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Other information
      </div>

      {/* Has policy been assigned? */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Has policy been assigned? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.policyAssigned as string) || ''}
            onChange={(e) => {
              onFieldChange('policyAssigned', e.target.value);
              if (e.target.value !== 'Yes') {
                onFieldChange('policyAssignedTo', undefined);
                onFieldChange('policyAssignedDate', undefined);
              }
            }}
            className={selectClass(!!(data.policyAssigned as string))}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
            ))}
          </select>
          {chevronIcon}
        </div>
      </div>

      {/* If Yes: To whom | Date assigned */}
      {policyAssigned && (
        <>
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                To whom has policy been assigned? <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.policyAssignedTo as string) || ''}
                onChange={(e) => onFieldChange('policyAssignedTo', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Date the policy was assigned <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.policyAssignedDate as string) || ''}
                  onChange={(e) => onFieldChange('policyAssignedDate', toIsoDate(e.target.value))}
                  className={dateInputClass(!!(data.policyAssignedDate as string))}
                />
                {calendarIcon('mask_assigned_date')}
              </div>
            </div>
          </div>

          {/* Other Details */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Other Details
            </div>
            <textarea
              placeholder="Other Details"
              maxLength={2000}
              value={(data.policyAssignedDetails as string) || ''}
              onChange={(e) => onFieldChange('policyAssignedDetails', e.target.value)}
              className={textareaClass}
            />
          </div>

          {/* Upload Documents */}
          <FileUploadField
            fieldKey="assignedDocUpload"
            files={pendingFiles?.get('assignedDocUpload')}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
          />
        </>
      )}

      {/* ══════════════ Divider ══════════════ */}
      <div className="w-full h-[1px] bg-[#E1E1E1]" />

      {/* Pension Adjustment Order */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Has a Pension Adjustment Order been issued or is in the process of being issued in respect of pension benefits held on behalf of this customer? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.pensionAdjustmentOrder as string) || ''}
            onChange={(e) => {
              onFieldChange('pensionAdjustmentOrder', e.target.value);
              if (e.target.value !== 'Yes') {
                onFieldChange('pensionAdjustmentDate', undefined);
                onFieldChange('pensionAdjustmentDetails', undefined);
              }
            }}
            className={selectClass(!!(data.pensionAdjustmentOrder as string))}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
            ))}
          </select>
          {chevronIcon}
        </div>
      </div>

      {/* If Yes: Date | Other Details | Upload */}
      {pensionAdjustment && (
        <>
          {/* Date of the Pension Adjustment Order */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Date of the Pension Adjustment Order <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <input
                type="date"
                value={(data.pensionAdjustmentDate as string) || ''}
                onChange={(e) => onFieldChange('pensionAdjustmentDate', toIsoDate(e.target.value))}
                className={dateInputClass(!!(data.pensionAdjustmentDate as string))}
              />
              {calendarIcon('mask_pension_adj_date')}
            </div>
          </div>

          {/* Other Details */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Other Details
            </div>
            <textarea
              placeholder="Other Details"
              maxLength={2000}
              value={(data.pensionAdjustmentDetails as string) || ''}
              onChange={(e) => onFieldChange('pensionAdjustmentDetails', e.target.value)}
              className={textareaClass}
            />
          </div>

          {/* Upload Documents */}
          <FileUploadField
            fieldKey="pensionAdjDocUpload"
            files={pendingFiles?.get('pensionAdjDocUpload')}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
          />
        </>
      )}

      {/* ══════════════ Divider ══════════════ */}
      <div className="w-full h-[1px] bg-[#E1E1E1]" />

      {/* ══════════════ Unique Death Benefit ══════════════ */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Is there a unique death benefit applying to the policy? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.uniqueDeathBenefit as string) || ''}
            onChange={(e) => onFieldChange('uniqueDeathBenefit', e.target.value)}
            className={selectClass(!!(data.uniqueDeathBenefit as string))}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            <option value="Yes" className="text-[#333]">Yes</option>
            <option value="No" className="text-[#333]">No</option>
          </select>
          {chevronIcon}
        </div>
      </div>

      {(data.uniqueDeathBenefit as string) === 'Yes' && (
        <>
          {/* Expiry date */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Expiry date of the unique death benefit <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <input
                type="date"
                value={(data.uniqueDeathBenefitExpiry as string) || ''}
                onChange={(e) => onFieldChange('uniqueDeathBenefitExpiry', toIsoDate(e.target.value))}
                className={dateInputClass(!!(data.uniqueDeathBenefitExpiry as string))}
              />
              {calendarIcon('mask_death_benefit_date')}
            </div>
          </div>

          {/* Please provide details */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Please provide details
            </div>
            <textarea
              placeholder="Please provide details"
              maxLength={2000}
              value={(data.uniqueDeathBenefitDetails as string) || ''}
              onChange={(e) => onFieldChange('uniqueDeathBenefitDetails', e.target.value)}
              className={textareaClass}
            />
          </div>
        </>
      )}
    </div>
  );
}
